import React, { useState, useEffect, useRef } from "react";
import { FiMail, FiPhone, FiMapPin, FiCalendar, FiClock, FiUser, FiEdit2, FiTrash2, FiPlus, FiFileText, FiDollarSign, FiDownload, FiEye, FiSend } from 'react-icons/fi';
import pdfService from '../../services/pdfService';
import { useAuth } from "../../context/AuthContext";
import { useTenant } from "../../context/TenantContext";
import clientService from "../../services/clientService";
import appointmentService from "../../services/appointmentService";
import invoiceService from "../../services/invoiceService";
import InvoiceModal from "../billing/InvoiceModal";
import { functions } from "../../config/firebase";
import { httpsCallable } from "firebase/functions";
import toast from "react-hot-toast";
import SoapNoteModal from "../notes/SoapNoteModal";
import { getStaff } from "../../services/staffService";
import { useUser } from "../../context/UserContext";
import InvoicePreviewModal from "../billing/InvoicePreviewModal";
import { formatCurrency } from "../../utils/currency";
import { tenantDataService } from "../../services/tenantDataService";
import ConfirmationModal from "../common/ConfirmationModal";
import { useConfirmation } from "../../hooks/useConfirmation";

// ===== TABS & HELPERS =====
const TABS = [
  { key: "overview", label: "Overview" },
  { key: "appointments", label: "Appointments" },
  { key: "notes", label: "Notes" },
  { key: "documents", label: "Documents" },
  { key: "billing", label: "Billing" },
  { key: "log", label: "Log" },
];

function formatDateDMY(dateStr) {
  if (!dateStr) return "";
  
  // Handle Firestore Timestamp objects
  if (dateStr.seconds) {
    dateStr = new Date(dateStr.seconds * 1000).toISOString();
  }
  
  // Handle Date objects
  if (dateStr instanceof Date) {
    dateStr = dateStr.toISOString();
  }
  
  const [year, month, day] = dateStr.split("T")[0].split("-");
  if (!day || !month || !year) return dateStr;
  return `${day.padStart(2, "0")}-${month.padStart(2, "0")}-${year}`;
}
function formatDateTimeDMY(dateStr) {
  if (!dateStr) return "";
  
  // Handle Firestore Timestamp objects
  if (dateStr.seconds) {
    dateStr = new Date(dateStr.seconds * 1000);
  }
  
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  return `${String(d.getDate()).padStart(2, "0")}-${String(
    d.getMonth() + 1
  ).padStart(2, "0")}-${d.getFullYear()}`;
}

function NoteStatus({ status, onClick, title }) {
  const map = {
    red: { color: "bg-red-500", label: "Not started" },
    amber: { color: "bg-yellow-400", label: "Draft" },
    green: { color: "bg-green-500", label: "Finalised" },
  };
  const val = map[status] || map.red;
  return (
    <span
      className={`flex items-center space-x-1 cursor-pointer ${onClick ? "hover:scale-110 transition" : ""}`}
      title={title || val.label}
      onClick={e => { e.stopPropagation(); if (onClick) onClick(); }}
      tabIndex={onClick ? 0 : undefined}
      role={onClick ? "button" : undefined}
    >
      <span className={`inline-block w-3 h-3 rounded-full ${val.color}`}></span>
      <span className="text-xs text-gray-500">{val.label}</span>
    </span>
  );
}

function parseQuery() {
  try {
    const params = {};
    window.location.search
      .replace(/^\?/, "")
      .split("&")
      .forEach((kv) => {
        if (kv) {
          const [k, v] = kv.split("=");
          params[decodeURIComponent(k)] = decodeURIComponent(v || "");
        }
      });
    return params;
  } catch {
    return {};
  }
}

// Remove localStorage sync - invoices are now handled by Firebase services
// This function is no longer needed as invoiceService handles all persistence

// ============ CLIENT PROFILE COMPONENT =============
function ClientProfile({ clients, onEdit, onDelete, onBack, onClientUpdate }) {
  const id = window.location.pathname.split("/").slice(-1)[0];
  const client = clients?.find((c) => c.id === id);
  const { user } = useUser();
  const { currentTenant, tenantSettings } = useTenant();
  const { confirmationState, showConfirmation, handleConfirm, handleCancel } = useConfirmation();

  const [tab, setTab] = useState("overview");
  const [appointments, setAppointments] = useState([]);
  const [soapNotes, setSoapNotes] = useState([]);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [staffList, setStaffList] = useState([]);
  const [bills, setBills] = useState([]);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedAppointmentForInvoice, setSelectedAppointmentForInvoice] = useState(null);
  const [logEntries, setLogEntries] = useState([]);
  const [logMessage, setLogMessage] = useState("");
  const [logTitle, setLogTitle] = useState("");
  const [logLoading, setLogLoading] = useState(false);
  const [editingLogId, setEditingLogId] = useState(null);
  const [emailingInvoices, setEmailingInvoices] = useState(new Set());

  // Rich text editor states
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [fontSize, setFontSize] = useState(14);
  const editorRef = useRef(null);
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [previewBill, setPreviewBill] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [clinicSettings, setClinicSettings] = useState(null);

  const [pendingOpenNoteId, setPendingOpenNoteId] = useState(null);
  
  // Treatment Complete Modal states
  const [showTreatmentCompleteModal, setShowTreatmentCompleteModal] = useState(false);
  const [treatmentOutcome, setTreatmentOutcome] = useState('');
  const [treatmentSessions, setTreatmentSessions] = useState('');
  const [treatmentNotes, setTreatmentNotes] = useState('');
  const [clientSatisfaction, setClientSatisfaction] = useState(5);
  const [followUpNeeded, setFollowUpNeeded] = useState(false);
  const [followUpDate, setFollowUpDate] = useState('');

  // ========== TREATMENT COMPLETE & REACTIVATION ==========
  const handleTreatmentComplete = async () => {
    try {
      const treatmentData = {
        isActive: false,
        treatmentCompleted: true,
        treatmentOutcome: {
          outcome: treatmentOutcome,
          totalSessions: parseInt(treatmentSessions) || 0,
          completedDate: new Date().toISOString(),
          notes: treatmentNotes,
          clientSatisfaction: clientSatisfaction,
          followUpNeeded: followUpNeeded,
          followUpDate: followUpDate || null,
          completedBy: user?.name || 'Unknown'
        },
        updatedAt: new Date()
      };

      await clientService.updateClient(currentTenant.id, client.id, treatmentData);
      
      // Log the treatment completion
      await tenantDataService.addClientLog(currentTenant.id, client.id, {
        title: 'Treatment Completed',
        message: `Treatment completed with outcome: ${treatmentOutcome}. Total sessions: ${treatmentSessions}`,
        timestamp: new Date(),
        userId: user?.uid,
        userName: user?.name || 'Unknown'
      });

      toast.success('Treatment marked as complete successfully');
      setShowTreatmentCompleteModal(false);
      
      // Reset form
      setTreatmentOutcome('');
      setTreatmentSessions('');
      setTreatmentNotes('');
      setClientSatisfaction(5);
      setFollowUpNeeded(false);
      setFollowUpDate('');
      
      // Refresh client data
      if (onClientUpdate) {
        onClientUpdate();
      }
    } catch (error) {
      console.error('Error completing treatment:', error);
      toast.error('Failed to complete treatment');
    }
  };

  const handleReactivateClient = async () => {
    try {
      const reactivationData = {
        isActive: true,
        reactivatedAt: new Date().toISOString(),
        reactivatedBy: user?.name || 'Unknown',
        updatedAt: new Date()
      };

      await clientService.updateClient(currentTenant.id, client.id, reactivationData);
      
      // Log the reactivation
      await tenantDataService.addClientLog(currentTenant.id, client.id, {
        title: 'Client Reactivated',
        message: 'Client has been reactivated for continued treatment',
        timestamp: new Date(),
        userId: user?.uid,
        userName: user?.name || 'Unknown'
      });

      toast.success('Client reactivated successfully');
      
      // Refresh client data
      if (onClientUpdate) {
        onClientUpdate();
      }
    } catch (error) {
      console.error('Error reactivating client:', error);
      toast.error('Failed to reactivate client');
    }
  };

  // ========== EMAIL INVOICE TO CLIENT ==========
  const handleEmailInvoice = async (bill) => {
    if (!bill || !client?.email) {
      toast.error("Cannot send email: missing invoice or client email");
      return;
    }

    if (emailingInvoices.has(bill.id)) {
      toast.error('Email is already being sent for this invoice');
      return;
    }

    setEmailingInvoices(prev => new Set(prev).add(bill.id));
    try {
      // Prepare client data
      const clientData = {
        email: client.email,
        name: client.name
      };

      // Prepare tenant data
      const tenantData = {
        id: currentTenant?.id,
        name: currentTenant?.name || 'Healthcare Clinic',
        email: currentTenant?.email || '',
        address: currentTenant?.address || '',
        phone: currentTenant?.phone || ''
      };

      await invoiceService.sendInvoiceEmail(bill.id, clientData, tenantData);
      toast.success(`Invoice ${bill.invoiceNumber} sent to ${client.name}!`);
      loadBills(); // Refresh to show email sent indicator
    } catch (error) {
      console.error('Error sending invoice email:', error);
      if (error.message.includes('sent recently')) {
        toast.error('Email was sent recently. Please wait before sending again.');
      } else {
        toast.error(`Failed to send invoice email: ${error.message}`);
      }
    } finally {
      setEmailingInvoices(prev => {
        const newSet = new Set(prev);
        newSet.delete(bill.id);
        return newSet;
      });
    }
  };

  // ========== CSV EXPORT HANDLER FOR FINANCIO ==========
  const handleExportInvoiceCSV = () => {
    if (!bills.length) {
      alert("No invoices to export.");
      return;
    }
    const bill = bills[0]; // latest at the top
    const csvRows = [
      ['Invoice Number', bill.invoiceNumber],
      ['Date', formatDateDMY(bill.date)],
      ['Due Date', formatDateDMY(bill.dueDate)],
      ['Recipient', bill.recipientName],
      ['Email', bill.recipientEmail],
      ['Total', bill.total],
      [],
      ['Description', 'Date', 'Amount'],
      ...bill.lineItems.map(li => [
        li.description, formatDateDMY(li.date), li.amount
      ])
    ];
    const csvContent = csvRows.map(r => r.join(',')).join('\r\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Invoice_${bill.invoiceNumber}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ========== DATA LOADING ==========
  useEffect(() => {
    const loadData = async () => {
      if (!client) return;

      try {
        // Load appointments using service
        const allAppointments = await appointmentService.getAllAppointments();
        const clientAppointments = allAppointments.filter(appt => appt.clientId === id);
        setAppointments(clientAppointments);

        // Load SOAP notes from Firebase
        const { getNotes } = await import('../../services/noteService');
        const allSoapNotes = await getNotes();
        const clientSoapNotes = allSoapNotes.filter(note => {
          const noteAppointment = clientAppointments.find(appt => appt.id === note.appointmentId);
          return noteAppointment !== undefined;
        });
        setSoapNotes(clientSoapNotes);

        // Load staff data
        const staffData = await getStaff();
        setStaffList(Array.isArray(staffData) ? staffData : []);

        // Load invoices using service
        const allInvoices = await invoiceService.getAllInvoices();
        const clientInvoices = allInvoices.filter(inv => {
          // Check if invoice is linked to any of this client's appointments
          return clientAppointments.some(appt => appt.id === inv.appointmentId);
        });

        // Load clinic settings
        const settings = await tenantDataService.getAll('settings');
        if (settings && settings.length > 0) {
          setClinicSettings(settings[0]);
        }
        setBills(clientInvoices);

        // Load client documents and logs from Firebase
        const { clientDocumentService } = await import('../../services/clientDocumentService');
        const { clientLogService } = await import('../../services/clientLogService');
        
        const [clientDocuments, clientLogs] = await Promise.all([
          clientDocumentService.getClientDocuments(id),
          clientLogService.getClientLogs(id)
        ]);
        
        setDocuments(clientDocuments);
        setLogEntries(clientLogs);

      } catch (error) {
        console.error('Error loading client data:', error);
        setAppointments([]);
        setSoapNotes([]);
        setStaffList([]);
        setBills([]);
      }
      const q = parseQuery();
      if (q.tab === "notes" && q.appt) {
        setTab("notes");
        setPendingOpenNoteId(q.appt);
      }
      if (q.tab === "billing" && q.appt) {
        setTab("billing");
        // Open invoice modal for the specific appointment
        setTimeout(() => {
          handleCreateInvoice(q.appt);
        }, 100);
      }
    };

    loadData();
    // eslint-disable-next-line
  }, [id, client]);

  // SOAP MODAL logic
  const closeSoapModal = () => {
    setShowNoteModal(false);
    setSelectedAppointment(null);
  };
  const openSoapModal = (appt) => {
    setSelectedAppointment(appt || null);
    setShowNoteModal(true);
  };

  useEffect(() => {
    if (pendingOpenNoteId && tab === "notes") {
      let appt = appointments.find((a) => a.id === pendingOpenNoteId);
      if (appt) {
        openSoapModal(appt);
      }
      setPendingOpenNoteId(null);
    }
    // eslint-disable-next-line
  }, [tab, pendingOpenNoteId, soapNotes, appointments]);



  // *** NEW ***: Handlers to update the UI instantly after modal actions
  const handleNoteSaved = (savedNote) => {
    setSoapNotes(prevNotes => {
      const noteExists = prevNotes.some(n => n.id === savedNote.id);
      if (noteExists) {
        return prevNotes.map(n => (n.id === savedNote.id ? savedNote : n));
      }
      return [savedNote, ...prevNotes];
    });
    // The modal's internal save function will also close it
  };

  const handleNoteDeleted = (deletedAppointmentId) => {
    setSoapNotes(prevNotes => prevNotes.filter(n => n.appointmentId !== deletedAppointmentId));
    // The modal's internal delete function will also close it
  };


  // --- Billing handlers ---
  const saveBills = async (items) => {
    setBills(items);
    // Invoices are now handled by invoiceService via Firebase
    // No localStorage needed - all data is tenant-scoped in Firebase
  };

  const loadBills = async () => {
    try {
      const allInvoices = await invoiceService.getAllInvoices();
      const clientInvoices = allInvoices.filter(inv => {
        // Check if invoice is linked to any of this client's appointments
        return appointments.some(appt => appt.id === inv.appointmentId);
      });
      setBills(clientInvoices);
    } catch (error) {
      console.error('Error loading bills:', error);
    }
  };

  const handleCreateInvoice = (appointmentId) => {
    const appointment = appointments.find(a => a.id === appointmentId);
    if (appointment) {
      // Add client info to appointment for InvoiceModal
      const appointmentWithClient = {
        ...appointment,
        client: {
          name: client.name,
          email: client.email || ''
        }
      };
      setSelectedAppointmentForInvoice(appointmentWithClient);
      setShowInvoiceModal(true);
    }
  };

  const handleInvoiceSaved = (savedInvoice) => {
    // Add the saved invoice to the bills list
    const updated = [savedInvoice, ...(Array.isArray(bills) ? bills : [])];
    saveBills(updated);
    setShowInvoiceModal(false);
    setSelectedAppointmentForInvoice(null);
  };

  const handleDeleteBill = async (billId) => {
    const confirmed = await showConfirmation({
      title: "Delete Invoice",
      message: "Delete this invoice? This cannot be undone.",
      confirmText: "Delete",
      cancelText: "Cancel",
      type: "danger"
    });
    
    if (!confirmed) return;
    const updated = (Array.isArray(bills) ? bills : []).filter((b) => b.id !== billId);
    saveBills(updated);
  };

  const handleMarkPaid = async (billId) => {
    try {
      // Update in Firebase first
      await invoiceService.updateInvoice(billId, {
        status: "paid",
        paymentDate: new Date().toISOString()
      });
      
      // Then update local state
      const updated = (Array.isArray(bills) ? bills : []).map((b) =>
        b.id === billId
          ? { ...b, status: "paid", paymentDate: new Date().toISOString() }
          : b
      );
      saveBills(updated);
      toast.success('Invoice marked as paid');
    } catch (error) {
      console.error('Error marking invoice as paid:', error);
      toast.error('Failed to update invoice status');
    }
  };

  const handleMarkUnpaid = async (billId) => {
    try {
      // Update in Firebase first
      await invoiceService.updateInvoice(billId, {
        status: "unpaid",
        paymentDate: undefined
      });
      
      // Then update local state
      const updated = (Array.isArray(bills) ? bills : []).map((b) =>
        b.id === billId
          ? { ...b, status: "unpaid", paymentDate: undefined }
          : b
      );
      saveBills(updated);
      toast.success('Invoice marked as unpaid');
    } catch (error) {
      console.error('Error marking invoice as unpaid:', error);
      toast.error('Failed to update invoice status');
    }
  };

  const handleVoidInvoice = async (bill) => {
    const confirmed = await showConfirmation({
      title: "Void Invoice",
      message: "Are you sure you want to void this invoice? Please provide a reason.",
      confirmText: "Void",
      cancelText: "Cancel",
      type: "danger"
    });
    
    if (!confirmed) return;
    
    const reason = prompt('Please enter a reason for voiding this invoice:');
    if (reason !== null) { // User didn't cancel
      try {
        await invoiceService.voidInvoice(bill.id, reason);
        loadBills(); // Reload bills after voiding
        toast.success('Invoice voided successfully');
      } catch (error) {
        console.error('Error voiding invoice:', error);
        toast.error('Failed to void invoice');
      }
    }
  };

  const handleSupersedeInvoice = async (bill) => {
    const confirmed = await showConfirmation({
      title: "Supersede Invoice",
      message: "Are you sure you want to supersede this invoice? Please provide a reason.",
      confirmText: "Supersede",
      cancelText: "Cancel",
      type: "warning"
    });
    
    if (!confirmed) return;
    
    const reason = prompt('Please enter a reason for superseding this invoice:');
    if (reason !== null) { // User didn't cancel
      try {
        await invoiceService.supersedeInvoice(bill.id, '', reason);
        loadBills(); // Reload bills after superseding
        toast.success('Invoice superseded successfully');
      } catch (error) {
        console.error('Error superseding invoice:', error);
        toast.error('Failed to supersede invoice');
      }
    }
  };

  const handleRevertInvoice = async (bill) => {
    const action = bill.status === 'void' ? 'revert this voided invoice' : 'revert this superseded invoice';
    const confirmed = await showConfirmation({
      title: "Revert Invoice",
      message: `Are you sure you want to ${action} back to its original status?`,
      confirmText: "Revert",
      cancelText: "Cancel",
      type: "info"
    });
    
    if (!confirmed) return;
    
    try {
      if (bill.status === 'void') {
        await invoiceService.revertVoidedInvoice(bill.id);
      } else if (bill.status === 'superseded') {
        await invoiceService.revertSupersededInvoice(bill.id);
      }
      loadBills(); // Reload bills after reverting
      toast.success('Invoice reverted successfully');
    } catch (error) {
      console.error('Error reverting invoice:', error);
      toast.error('Failed to revert invoice');
    }
  };

  const saveLogEntries = async (entries) => {
    setLogEntries(entries);
    // Save to Firebase instead of localStorage
    const { clientLogService } = await import('../../services/clientLogService');
    
    // For new entries, create them in Firebase
    for (const entry of entries) {
      if (!entry.id || entry.id.toString().startsWith('temp_')) {
        await clientLogService.createLog({
          ...entry,
          clientId: id
        });
      }
    }
  };

  // DOCUMENTS TAB: Handlers
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);

    const reader = new FileReader();
    reader.onload = async () => {
      const newDoc = {
        id: Date.now().toString(),
        name: file.name,
        type: file.type,
        size: file.size,
        uploadedAt: new Date().toISOString(),
        uploadedBy: user.name,
        uploadedByRole: user.role,
        data: reader.result,
      };
      const updatedDocs = [newDoc, ...documents];
      setDocuments(updatedDocs);
      
      // Save to Firebase instead of localStorage
      const { clientDocumentService } = await import('../../services/clientDocumentService');
      await clientDocumentService.createDocument({
        ...newDoc,
        clientId: id
      });
      
      setUploading(false);
    };
    reader.readAsDataURL(file);
  };

  const handleDownload = (doc) => {
    const link = document.createElement("a");
    link.href = doc.data;
    link.download = doc.name;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const deleteDocument = async (docId) => {
    const confirmed = await showConfirmation({
      title: "Delete Document",
      message: "Delete this file? This cannot be undone.",
      confirmText: "Delete",
      cancelText: "Cancel",
      type: "danger"
    });
    
    if (!confirmed) return;
    const updatedDocs = documents.filter((d) => d.id !== docId);
    setDocuments(updatedDocs);
    
    // Delete from Firebase instead of localStorage
    const { clientDocumentService } = await import('../../services/clientDocumentService');
    await clientDocumentService.deleteDocument(docId);
  };

  // --- FILTERED DATA ---
  const clientAppointments = client ? appointments
    .filter((appt) => {
      console.log(`🔍 Filtering appointment ${appt.id}: clientId=${appt.clientId}, client.id=${client.id}, match=${appt.clientId === client.id}`);
      return appt.clientId === client.id;
    })
    .sort((a, b) => {
      const dateA = new Date(a.date + "T" + (a.time || "00:00"));
      const dateB = new Date(b.date + "T" + (b.time || "00:00"));
      return dateB - dateA;
    }) : [];

  console.log(`📋 ClientProfile: Found ${clientAppointments.length} appointments for client ${client?.id || 'undefined'}`);

  const clientNotes = client ? soapNotes
    .filter((note) => note.clientId === client.id)
    .sort((a, b) => {
      const dateA = new Date(a.date + "T" + (a.time || "00:00"));
      const dateB = new Date(b.date + "T" + (b.time || "00:00"));
      return dateB - dateA;
    }) : [];

  const sortedDocuments = [...documents].sort(
    (a, b) => new Date(b.uploadedAt) - new Date(a.uploadedAt)
  );

  const sortedBills = [...bills].sort((a, b) => {
    // Extract numeric part from invoice numbers (e.g., "INV-0000006" -> 6)
    const getInvoiceNumber = (invoiceNum) => {
      if (!invoiceNum) return 0;
      const match = invoiceNum.match(/(\d+)$/);
      return match ? parseInt(match[1], 10) : 0;
    };
    
    const aNum = getInvoiceNumber(a.invoiceNumber);
    const bNum = getInvoiceNumber(b.invoiceNumber);
    
    // Sort by invoice number descending (highest first)
    return bNum - aNum;
  });

  const sortedLogEntries = [...logEntries].sort(
    (a, b) => new Date(b.time) - new Date(a.time)
  );

  // --- Click-through: Handle Note/Invoice status clicks in Appointments tab ---
  const handleNoteStatusClick = (appt) => {
    openSoapModal(appt);
    setTab("notes");
  };

  const handleInvoiceStatusClick = (appt) => {
    const bill = bills.find(b => b.appointmentId === appt.id);
    if (bill) {
      // If invoice exists, show preview
      setTab("billing");
      setTimeout(() => {
        setPreviewBill(bill);
      }, 100);
    } else {
      // If no invoice exists, open invoice modal to create one
      handleCreateInvoice(appt.id);
    }
  };

  const handleOpenNote = (appt) => {
    openSoapModal(appt);
  };

  const handleViewNote = (note) => {
    openSoapModal(appointments.find((a) => a.id === note.appointmentId) || null);
  };

  const handleViewInCalendar = (appt) => {
    window.location.href = `/calendar?date=${appt.date}`;
  };

  const handleAddLogEntry = (e) => {
    e.preventDefault();
    if (!logMessage.trim()) return;
    setLogLoading(true);

    if (editingLogId) {
      // Update existing entry
      const updated = logEntries.map(entry =>
        entry.id === editingLogId
          ? {
            ...entry,
            title: logTitle.trim() || "Untitled Note",
            message: logMessage.trim(),
            editedAt: new Date().toISOString(),
            editedBy: user.name
          }
          : entry
      );
      saveLogEntries(updated);
      setEditingLogId(null);
    } else {
      // Create new entry
      const newEntry = {
        id: Date.now().toString(),
        title: logTitle.trim() || "Untitled Note",
        message: logMessage.trim(),
        author: user.name,
        role: user.role,
        time: new Date().toISOString(),
      };
      const updated = [newEntry, ...logEntries];
      saveLogEntries(updated);
    }

    setLogMessage("");
    setLogTitle("");
    setLogLoading(false);

    // Reset rich text editor formatting
    setIsBold(false);
    setIsItalic(false);
    setIsUnderline(false);
    setFontSize(14);
  };

  const handleEditLogEntry = (entry) => {
    setEditingLogId(entry.id);
    setLogTitle(entry.title || "");
    setLogMessage(entry.message);
    if (editorRef.current) {
      editorRef.current.innerHTML = entry.message;
      editorRef.current.focus();
    }
  };

  const handleDeleteLogEntry = async (entryId) => {
    const confirmed = await showConfirmation({
      title: "Delete Log Entry",
      message: "Delete this log entry? This cannot be undone.",
      confirmText: "Delete",
      cancelText: "Cancel",
      type: "danger"
    });
    
    if (!confirmed) return;
    const updated = logEntries.filter(entry => entry.id !== entryId);
    saveLogEntries(updated);
  };

  const handleCancelEdit = () => {
    setEditingLogId(null);
    setLogTitle("");
    setLogMessage("");
    if (editorRef.current) {
      editorRef.current.innerHTML = "";
    }
    // Reset rich text editor formatting
    setIsBold(false);
    setIsItalic(false);
    setIsUnderline(false);
    setFontSize(14);
  };

  const getNoteStatus = (appt) => {
    const note = soapNotes.find(n => n.appointmentId === appt.id);
    if (!note) return "red";
    return note.finalised ? "green" : "amber";
  };
  
  const getInvoiceStatus = (appt) => {
    const bill = bills.find(b => b.appointmentId === appt.id);
    if (!bill) return "red";
    return bill.status === "paid" ? "green" : "amber";
  };

  if (!client) {
    return (
      <div className="p-4">
        <p className="text-red-500">Client not found.</p>
        <button
          className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
          onClick={onBack}
        >
          ← Back to Clients
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-8 py-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <button
              onClick={onBack}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              ←
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Client Profile</h1>
              <p className="text-sm text-gray-500">{client.name}</p>
            </div>
          </div>
          <button
            onClick={onBack}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
          >
            Back to Clients
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="px-8">
          <nav className="flex space-x-8">
            {TABS.map((t) => (
              <button
                key={t.key}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${tab === t.key
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                onClick={() => setTab(t.key)}
              >
                {t.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="px-8 py-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {/* Overview Tab */}
          {tab === "overview" && (
            <div className="p-8 space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Personal Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Personal Information</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Full Name</label>
                      <p className="text-gray-900 font-medium">{client.name}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Email</label>
                      <p className="text-gray-900">{client.email}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Date of Birth</label>
                      <p className="text-gray-900">{formatDateDMY(client.dob) || 'Not provided'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Phone</label>
                      <p className="text-gray-900">{client.phone}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Gender</label>
                      <p className="text-gray-900">{client.gender || 'Not specified'}</p>
                    </div>
                  </div>
                </div>

                {/* Contact & Emergency Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact & Emergency</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Emergency Contact</label>
                      <p className="text-gray-900">{client.emergencyContact || 'Not provided'}</p>
                    </div>
                    {client.emergencyPhone && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Emergency Phone</label>
                        <p className="text-gray-900">{client.emergencyPhone}</p>
                      </div>
                    )}
                    <div>
                      <label className="text-sm font-medium text-gray-500">Address</label>
                      <p className="text-gray-900 whitespace-pre-line">{client.address || 'Not provided'}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Notes Section */}
              {client.notes && (
                <div className="mt-8 pt-8 border-t border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Notes</h3>
                  <div className="bg-gray-50 rounded-lg p-6">
                    <p className="text-gray-700">{client.notes}</p>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="mt-10 pt-8 border-t border-gray-200 flex gap-4">
                <button
                  onClick={() => onEdit(client)}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-600 transition-colors"
                >
                  Edit Client
                </button>
                <button
                  onClick={() => {
                    if (client) {
                      onDelete(client);
                    } else {
                      console.error('Cannot delete: client is undefined');
                    }
                  }}
                  className="px-4 py-2 border border-red-300 text-red-700 rounded-lg font-medium hover:bg-red-50 transition-colors"
                  disabled={!client}
                >
                  Delete Client
                </button>
                
                {/* Client Lifecycle Management */}
                {client?.isActive !== false ? (
                  <button
                    onClick={() => setShowTreatmentCompleteModal(true)}
                    className="px-4 py-2 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 transition-colors"
                  >
                    Treatment Complete
                  </button>
                ) : (
                  <button
                    onClick={() => handleReactivateClient()}
                    className="px-4 py-2 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 transition-colors"
                  >
                    Reactivate Client
                  </button>
                )}
              </div>
            </div>
          )}
          {/* Appointments Tab */}
          {tab === "appointments" && (
            <div className="p-8 space-y-8">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold text-gray-900">Appointments</h2>
                <span className="text-sm text-gray-500">{clientAppointments.length} total</span>
              </div>
              {clientAppointments.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-gray-400 text-4xl mb-4">📅</div>
                  <p className="text-gray-500 text-lg">No appointments found</p>
                  <p className="text-gray-400 text-sm">This client hasn't scheduled any appointments yet.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {clientAppointments.map((appt) => {
                    const staff = staffList.find((s) => s.id === appt.staffId);
                    return (
                      <div
                        key={appt.id}
                        className="bg-gray-50 border border-gray-200 rounded-lg p-6 hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <span className="font-semibold text-gray-900">{formatDateDMY(appt.date)}</span>
                              <span className="text-gray-600">{appt.time}</span>
                              <span className={`px-2 py-1 text-xs font-medium rounded-full ${appt.status === 'completed' ? 'bg-green-100 text-green-800' :
                                  appt.status === 'scheduled' ? 'bg-blue-100 text-blue-800' :
                                    appt.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                                      'bg-gray-100 text-gray-800'
                                }`}>
                                {appt.status}
                              </span>
                            </div>
                            <div className="flex items-center space-x-4 text-sm text-gray-600">
                              <span>Clinician: {staff ? staff.name : "Not assigned"}</span>
                              {appt.type && <span>Type: {appt.type}</span>}
                              {appt.room && <span>Room: {appt.room}</span>}
                            </div>
                          </div>
                          <div className="flex items-center space-x-3">
                            <div className="flex items-center space-x-2">
                              <span className="text-xs text-gray-500">Note:</span>
                              <NoteStatus
                                status={getNoteStatus(appt)}
                                title="Click to view SOAP note"
                                onClick={() => handleNoteStatusClick(appt)}
                              />
                            </div>
                            <div className="flex items-center space-x-2">
                              <span className="text-xs text-gray-500">Invoice:</span>
                              <NoteStatus
                                status={getInvoiceStatus(appt)}
                                title="Click to view Invoice"
                                onClick={() => handleInvoiceStatusClick(appt)}
                              />
                            </div>
                            <div className="flex space-x-2">
                              <button
                                className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                title="Open SOAP Note"
                                onClick={() => handleOpenNote(appt)}
                              >
                                📝
                              </button>
                              <button
                                className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                title="View in Calendar"
                                onClick={() => handleViewInCalendar(appt)}
                              >
                                📅
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          {/* SOAP Notes Tab */}
          {tab === "notes" && (
            <div className="p-8 space-y-8">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold text-gray-900">SOAP Notes</h2>
                <span className="text-sm text-gray-500">{clientNotes.length} total</span>
              </div>
              {clientNotes.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-gray-400 text-4xl mb-4">📝</div>
                  <p className="text-gray-500 text-lg">No SOAP notes found</p>
                  <p className="text-gray-400 text-sm">Clinical notes will appear here once created.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {clientNotes.map((note) => {
                    const staff = staffList.find((s) => s.id === note.clinicianId);
                    return (
                      <div
                        key={note.id}
                        className="bg-gray-50 border border-gray-200 rounded-lg p-4 cursor-pointer hover:bg-gray-100 transition-colors"
                        onClick={() => handleViewNote(note)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <span className="font-semibold text-gray-900">{formatDateDMY(note.date)}</span>
                              <span className="text-gray-600">{note.time}</span>
                              <span className={`px-2 py-1 text-xs font-medium rounded-full ${note.finalised ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                                }`}>
                                {note.finalised ? "Finalised" : "Draft"}
                              </span>
                            </div>
                            <div className="text-sm text-gray-600">
                              Clinician: {note.finalised ? note.finalisedBy : staff ? staff.name : "Not assigned"}
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <NoteStatus status={note.finalised ? "green" : "amber"} />
                            <button className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                              →
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          {/* Documents Tab */}
          {tab === "documents" && (
            <div className="p-8 space-y-8">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold text-gray-900">Documents</h2>
                <span className="text-sm text-gray-500">{sortedDocuments.length} total</span>
              </div>
              {(user.role === "admin" || user.role === "staff" || user.role === "clinician") && (
                <div className="bg-gray-50 p-6 rounded-lg border-2 border-dashed border-gray-300 hover:border-gray-400 transition-colors">
                  <label className="block font-medium mb-4">
                    Upload new file
                    <input
                      type="file"
                      className="block mt-4 w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                      onChange={handleFileUpload}
                      disabled={uploading}
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.txt"
                    />
                  </label>
                  <p className="text-sm text-gray-500 mt-2">
                    Supported formats: PDF, JPG, PNG, DOC, DOCX, TXT (Max 10MB)
                  </p>
                </div>
              )}
              {sortedDocuments.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-gray-400 text-lg mb-2">📄</div>
                  <div className="text-gray-400 italic">No documents uploaded yet.</div>
                </div>
              ) : (
                <ul className="space-y-4">
                  {sortedDocuments.map((doc) => (
                    <li key={doc.id} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="mb-2">
                            <span className="font-semibold text-lg">{doc.name}</span>
                            <span className="ml-3 text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded">
                              {Math.round(doc.size / 1024)} KB • {doc.type || "file"}
                            </span>
                          </div>
                          <div className="text-sm text-gray-600">
                            Uploaded {formatDateTimeDMY(doc.uploadedAt)} by {doc.uploadedBy} ({doc.uploadedByRole})
                          </div>
                        </div>
                        <div className="flex space-x-3 ml-6">
                          <button
                            className="px-4 py-2 bg-blue-100 rounded-lg text-blue-700 hover:bg-blue-200 transition-colors font-medium"
                            onClick={() => setPreviewDoc(doc)}
                            title="View file"
                          >
                            View
                          </button>
                          <button
                            className="px-4 py-2 bg-blue-100 rounded-lg text-blue-700 hover:bg-blue-200 transition-colors font-medium"
                            onClick={() => handleDownload(doc)}
                            title="Download file"
                          >
                            Download
                          </button>
                          {user.role === "admin" && (
                            <button
                              className="px-4 py-2 bg-red-100 rounded-lg text-red-700 hover:bg-red-200 transition-colors font-medium"
                              onClick={() => deleteDocument(doc.id)}
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          {/* Billing Tab */}
          {tab === "billing" && (
            <div className="p-8 space-y-8">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold text-gray-900">Billing & Invoices</h2>
                <span className="text-sm text-gray-500">{sortedBills.length} total</span>
              </div>

              {/* Create Invoice Section */}
              {(user.role === "admin" || user.role === "staff" || user.role === "clinician") && (
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Create New Invoice</h3>
                  <p className="text-gray-600 mb-4">Select an appointment to create an invoice:</p>
                  {appointments.length === 0 ? (
                    <div className="text-center py-8">
                      <div className="text-gray-400 text-2xl mb-2">📅</div>
                      <p className="text-gray-500">No appointments found for this client</p>
                      <p className="text-gray-400 text-sm">Create appointments in the calendar first</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {appointments
                        .sort((a, b) => {
                          // Sort by date first (newest first), then by time
                          const dateA = new Date(a.date + 'T' + a.time);
                          const dateB = new Date(b.date + 'T' + b.time);
                          return dateB - dateA; // Newest first
                        })
                        .map(appt => (
                        <button
                          key={appt.id}
                          onClick={() => handleCreateInvoice(appt.id)}
                          className="p-4 text-left border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-colors"
                        >
                          <div className="font-medium text-gray-900">{formatDateDMY(appt.date)} {appt.time}</div>
                          <div className="text-sm text-gray-600">{appt.type}</div>
                          <div className="text-xs text-gray-500 mt-1">
                            {bills.find(b => b.appointmentId === appt.id) ? '✓ Invoice exists' : 'No invoice yet'}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Existing Invoices */}
              {sortedBills.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-gray-400 text-4xl mb-4">💰</div>
                  <p className="text-gray-500 text-lg">No invoices found</p>
                  <p className="text-gray-400 text-sm">Invoices will appear here once created.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {sortedBills.map((bill) => (
                    <div
                      key={bill.id}
                      className="bg-gray-50 border border-gray-200 rounded-lg p-6 hover:bg-gray-100 transition-colors cursor-pointer"
                      onClick={() => setPreviewBill(bill)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <span className="font-semibold text-gray-900">{bill.invoiceNumber}</span>
                            <span className="text-gray-600">{formatDateDMY(bill.date)}</span>
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                              bill.status === 'paid' ? 'bg-emerald-100 text-emerald-800' :
                              bill.status === 'unpaid' ? 'bg-red-100 text-red-800' :
                              bill.status === 'void' ? 'bg-gray-100 text-gray-800' :
                              bill.status === 'superseded' ? 'bg-amber-100 text-amber-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {bill.status.toUpperCase()}
                            </span>
                          </div>
                          <div className="flex items-center space-x-4 text-sm text-gray-600">
                            <span>To: {bill.recipientName || bill.clientName}</span>
                            <span>Total: £{bill.total}</span>
                            {bill.dueDate && <span>Due: {formatDateDMY(bill.dueDate)}</span>}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2 flex-wrap">
                          <button
                            className={`px-3 py-1.5 rounded-lg flex items-center gap-1 text-sm font-medium transition-colors ${
                              emailingInvoices.has(bill.id)
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                            }`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEmailInvoice(bill);
                            }}
                            disabled={emailingInvoices.has(bill.id)}
                            title={bill.lastEmailSent ? `Last sent: ${new Date(bill.lastEmailSent.seconds ? bill.lastEmailSent.seconds * 1000 : bill.lastEmailSent).toLocaleString()}` : "Email invoice to client"}
                          >
                            {emailingInvoices.has(bill.id) ? (
                              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-400"></div>
                            ) : (
                              <>
                                <FiMail className="w-3 h-3" />
                                {bill.lastEmailSent && (
                                  <span className="w-2 h-2 bg-green-500 rounded-full" title="Email sent"></span>
                                )}
                              </>
                            )}
                            {emailingInvoices.has(bill.id) ? 'Sending...' : 'Email'}
                          </button>
                          {bill.status === "unpaid" && (user.role === "admin" || user.role === "super_admin") && (
                            <button
                              className="px-3 py-1.5 bg-emerald-100 rounded-lg text-emerald-700 hover:bg-emerald-200 text-sm font-medium transition-colors"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMarkPaid(bill.id);
                              }}
                            >
                              Mark Paid
                            </button>
                          )}
                          {bill.status === "paid" && (user.role === "admin" || user.role === "super_admin") && (
                            <button
                              className="px-3 py-1.5 bg-amber-100 rounded-lg text-amber-700 hover:bg-amber-200 text-sm font-medium transition-colors"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMarkUnpaid(bill.id);
                              }}
                            >
                              Mark Unpaid
                            </button>
                          )}
                          {(bill.status === "paid" || bill.status === "unpaid") && (user.role === "admin" || user.role === "super_admin") && (
                            <>
                              <button
                                className="px-3 py-1.5 bg-red-100 rounded-lg text-red-700 hover:bg-red-200 text-sm font-medium transition-colors"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleVoidInvoice(bill);
                                }}
                                title="Void invoice"
                              >
                                Void
                              </button>
                              <button
                                className="px-3 py-1.5 bg-amber-100 rounded-lg text-amber-700 hover:bg-amber-200 text-sm font-medium transition-colors"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSupersedeInvoice(bill);
                                }}
                                title="Supersede invoice"
                              >
                                Supersede
                              </button>
                            </>
                          )}
                          {(bill.status === "void" || bill.status === "superseded") && (user.role === "admin" || user.role === "super_admin") && (
                            <button
                              className="px-3 py-1.5 bg-green-100 rounded-lg text-green-700 hover:bg-green-200 text-sm font-medium transition-colors"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRevertInvoice(bill);
                              }}
                              title="Revert invoice to original status"
                            >
                              Revert
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {/* General Log Tab */}
          {tab === "log" && (
            <div className="p-8 space-y-8">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold text-gray-900">General Log</h2>
                <span className="text-sm text-gray-500">{sortedLogEntries.length} total</span>
              </div>
              {(user.role === "admin" || user.role === "staff" || user.role === "clinician") && (
                <form
                  onSubmit={handleAddLogEntry}
                  className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 space-y-4"
                >
                  {/* Title Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Note Title
                    </label>
                    <input
                      type="text"
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="Enter a title for this note..."
                      value={logTitle}
                      onChange={(e) => setLogTitle(e.target.value)}
                      disabled={logLoading}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {editingLogId ? 'Edit note content' : 'Add a general note (visible to all staff)'}
                    </label>

                    {/* Rich Text Editor Toolbar */}
                    <div className="border border-gray-300 rounded-t-lg bg-gray-50 p-2 flex flex-wrap gap-1">
                      {/* Bold */}
                      <button
                        type="button"
                        onClick={() => {
                          if (editorRef.current) {
                            editorRef.current.focus();
                            document.execCommand('bold', false, null);
                            setIsBold(document.queryCommandState('bold'));
                          }
                        }}
                        className={`px-3 py-1 text-sm font-bold border border-gray-300 rounded transition-colors ${isBold ? 'bg-blue-200 border-blue-400' : 'hover:bg-gray-200'
                          }`}
                        title="Bold"
                      >
                        B
                      </button>

                      {/* Italic */}
                      <button
                        type="button"
                        onClick={() => {
                          if (editorRef.current) {
                            editorRef.current.focus();
                            document.execCommand('italic', false, null);
                            setIsItalic(document.queryCommandState('italic'));
                          }
                        }}
                        className={`px-3 py-1 text-sm italic border border-gray-300 rounded transition-colors ${isItalic ? 'bg-blue-200 border-blue-400' : 'hover:bg-gray-200'
                          }`}
                        title="Italic"
                      >
                        I
                      </button>

                      {/* Underline */}
                      <button
                        type="button"
                        onClick={() => {
                          if (editorRef.current) {
                            editorRef.current.focus();
                            document.execCommand('underline', false, null);
                            setIsUnderline(document.queryCommandState('underline'));
                          }
                        }}
                        className={`px-3 py-1 text-sm underline border border-gray-300 rounded transition-colors ${isUnderline ? 'bg-blue-200 border-blue-400' : 'hover:bg-gray-200'
                          }`}
                        title="Underline"
                      >
                        U
                      </button>

                      <div className="border-l border-gray-400 mx-1"></div>

                      {/* Font Size */}
                      <select
                        value={fontSize}
                        onChange={(e) => {
                          const newSize = parseInt(e.target.value);
                          setFontSize(newSize);
                          if (editorRef.current) {
                            editorRef.current.focus();
                            // Convert px to HTML font size (1-7 scale)
                            const htmlSize = Math.min(7, Math.max(1, Math.round(newSize / 3)));
                            document.execCommand('fontSize', false, htmlSize);
                          }
                        }}
                        className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-200 transition-colors"
                        title="Font Size"
                      >
                        <option value="8">8px</option>
                        <option value="9">9px</option>
                        <option value="10">10px</option>
                        <option value="11">11px</option>
                        <option value="12">12px</option>
                        <option value="14">14px</option>
                        <option value="16">16px</option>
                        <option value="18">18px</option>
                        <option value="20">20px</option>
                      </select>

                      <div className="border-l border-gray-400 mx-1"></div>

                      {/* Bullet Points */}
                      <button
                        type="button"
                        onClick={() => {
                          if (editorRef.current) {
                            const editor = editorRef.current;
                            const selection = window.getSelection();
                            const range = selection.getRangeAt(0);

                            // Create bullet point
                            const bulletText = '• ';
                            const textNode = document.createTextNode(bulletText);
                            range.insertNode(textNode);

                            // Move cursor after bullet
                            range.setStartAfter(textNode);
                            range.collapse(true);
                            selection.removeAllRanges();
                            selection.addRange(range);

                            editor.focus();
                          }
                        }}
                        className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-200 transition-colors"
                        title="Add Bullet Point"
                      >
                        Bullet
                      </button>

                      {/* Numbered List */}
                      <button
                        type="button"
                        onClick={() => {
                          if (editorRef.current) {
                            const editor = editorRef.current;
                            const selection = window.getSelection();
                            const range = selection.getRangeAt(0);

                            // Create numbered point
                            const numberedText = '1. ';
                            const textNode = document.createTextNode(numberedText);
                            range.insertNode(textNode);

                            // Move cursor after number
                            range.setStartAfter(textNode);
                            range.collapse(true);
                            selection.removeAllRanges();
                            selection.addRange(range);

                            editor.focus();
                          }
                        }}
                        className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-200 transition-colors"
                        title="Add Numbered Item"
                      >
                        Num
                      </button>
                    </div>

                    {/* Rich Text Editor */}
                    <div
                      ref={editorRef}
                      contentEditable
                      spellCheck={true}
                      className="w-full border border-gray-300 border-t-0 rounded-b-lg px-4 py-3 min-h-[100px] focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-vertical overflow-auto"
                      style={{
                        outline: 'none',
                        direction: 'ltr',
                        textAlign: 'left'
                      }}
                      onInput={(e) => {
                        setLogMessage(e.target.innerHTML);
                      }}
                      onBlur={(e) => {
                        setLogMessage(e.target.innerHTML);
                      }}
                      onKeyUp={() => {
                        // Update button states based on current cursor position
                        setIsBold(document.queryCommandState('bold'));
                        setIsItalic(document.queryCommandState('italic'));
                        setIsUnderline(document.queryCommandState('underline'));
                      }}
                      onMouseUp={() => {
                        // Update button states when clicking around
                        setIsBold(document.queryCommandState('bold'));
                        setIsItalic(document.queryCommandState('italic'));
                        setIsUnderline(document.queryCommandState('underline'));
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          // Allow normal Enter behavior for line breaks
                          e.stopPropagation();
                        }
                      }}
                      suppressContentEditableWarning={true}
                      disabled={logLoading}
                      data-placeholder="Enter your note here..."
                    />

                    {/* CSS for placeholder */}
                    <style>{`
                    [contenteditable][data-placeholder]:empty:before {
                      content: attr(data-placeholder);
                      color: #9CA3AF;
                      pointer-events: none;
                      position: absolute;
                    }
                  `}</style>
                  </div>
                  <div className="flex justify-end gap-3">
                    {editingLogId && (
                      <button
                        type="button"
                        onClick={handleCancelEdit}
                        className="px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors font-medium"
                        disabled={logLoading}
                      >
                        Cancel
                      </button>
                    )}
                    <button
                      type="submit"
                      className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={logLoading || !logMessage.trim()}
                    >
                      {logLoading ? (editingLogId ? 'Updating...' : 'Adding...') : (editingLogId ? 'Update Note' : 'Add Note')}
                    </button>
                  </div>
                </form>
              )}
              {sortedLogEntries.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-gray-400 text-lg mb-2">📝</div>
                  <div className="text-gray-400 italic">No log entries yet.</div>
                </div>
              ) : (
                <ul className="space-y-4">
                  {sortedLogEntries.map((entry) => (
                    <li
                      key={entry.id}
                      className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
                    >
                      {/* Note Header with Title and Actions */}
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-gray-900 mb-1">
                            {entry.title || 'Untitled Note'}
                          </h3>
                          {entry.editedAt && (
                            <div className="text-xs text-gray-400 mb-2">
                              Edited {formatDateTimeDMY(entry.editedAt)} by {entry.editedBy}
                            </div>
                          )}
                        </div>

                        {/* Edit/Delete Buttons - Only show if current user is author or admin */}
                        {(user.role === 'admin' || entry.author === user.name) && (
                          <div className="flex gap-2 ml-4">
                            <button
                              onClick={() => handleEditLogEntry(entry)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Edit Note"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleDeleteLogEntry(entry.id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete Note"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Note Content */}
                      <div
                        className="mb-3 text-gray-900 leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: entry.message }}
                      />

                      {/* Note Footer */}
                      <div className="text-sm text-gray-500 flex items-center space-x-2">
                        <span>{formatDateTimeDMY(entry.time)}</span>
                        <span>•</span>
                        <span className="font-medium">{entry.author}</span>
                        <span className="bg-gray-100 px-2 py-1 rounded text-xs">{entry.role}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          {/* Fallback for future tabs */}
          {tab !== "overview" &&
            tab !== "appointments" &&
            tab !== "notes" &&
            tab !== "documents" &&
            tab !== "billing" &&
            tab !== "log" && (
              <div className="text-gray-400 italic">
                {tab.charAt(0).toUpperCase() + tab.slice(1)} coming soon.
              </div>
            )}
        </div>
      </div>

      {/* Invoice Preview Modal */}
      {previewBill && (
        <InvoicePreviewModal
          bill={previewBill}
          client={client}
          appointment={appointments.find(a => a.id === previewBill.appointmentId)}
          onClose={() => setPreviewBill(null)}
          onRefresh={loadBills}
          userRole={user?.role}
        />
      )}

      {/* SOAP Note Modal */}
      {showNoteModal && (
        <SoapNoteModal
          appointment={selectedAppointment}
          client={client}
          onClose={closeSoapModal}
          onNoteSaved={handleNoteSaved}
          onNoteDeleted={handleNoteDeleted}
        />
      )}

      {/* Invoice Modal */}
      {showInvoiceModal && (
        <InvoiceModal
          appointment={selectedAppointmentForInvoice}
          onClose={() => {
            setShowInvoiceModal(false);
            setSelectedAppointmentForInvoice(null);
          }}
          onSave={handleInvoiceSaved}
        />
      )}

      {/* Document Viewer Modal */}
      {previewDoc && (
        <DocumentPreviewModal
          doc={previewDoc}
          onClose={() => setPreviewDoc(null)}
        />
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmationState.isOpen}
        onClose={handleCancel}
        onConfirm={handleConfirm}
        title={confirmationState.title}
        message={confirmationState.message}
        confirmText={confirmationState.confirmText}
        cancelText={confirmationState.cancelText}
        type={confirmationState.type}
      />

      {/* Hidden Invoice Components for PDF Generation - Hidden from print */}
      <div className="print:hidden">
        {bills.map((bill) => {
          const appointment = appointments.find(appt => appt.id === bill.appointmentId);
          return (
            <HiddenInvoiceForPDF
              key={`hidden-invoice-${bill.id}`}
              bill={bill}
              client={client}
              appointment={appointment}
              clinicSettings={clinicSettings}
              currentTenant={currentTenant}
            />
          );
        })}
      </div>

      {/* Treatment Complete Modal */}
      {showTreatmentCompleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Complete Treatment</h3>
            
            <div className="space-y-4">
              {/* Treatment Outcome */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Treatment Outcome *
                </label>
                <select
                  value={treatmentOutcome}
                  onChange={(e) => setTreatmentOutcome(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select outcome...</option>
                  <option value="successful">Successful - Goals achieved</option>
                  <option value="partial_success">Partial success - Some improvement</option>
                  <option value="referred_out">Referred to specialist</option>
                  <option value="discontinued">Discontinued by client</option>
                  <option value="no_improvement">No significant improvement</option>
                </select>
              </div>

              {/* Total Sessions */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Total Sessions Completed
                </label>
                <input
                  type="number"
                  value={treatmentSessions}
                  onChange={(e) => setTreatmentSessions(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter number of sessions"
                  min="0"
                />
              </div>

              {/* Client Satisfaction */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Client Satisfaction (1-5)
                </label>
                <select
                  value={clientSatisfaction}
                  onChange={(e) => setClientSatisfaction(parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value={1}>1 - Very Dissatisfied</option>
                  <option value={2}>2 - Dissatisfied</option>
                  <option value={3}>3 - Neutral</option>
                  <option value={4}>4 - Satisfied</option>
                  <option value={5}>5 - Very Satisfied</option>
                </select>
              </div>

              {/* Follow-up Needed */}
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={followUpNeeded}
                    onChange={(e) => setFollowUpNeeded(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Follow-up needed</span>
                </label>
              </div>

              {/* Follow-up Date */}
              {followUpNeeded && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Follow-up Date
                  </label>
                  <input
                    type="date"
                    value={followUpDate}
                    onChange={(e) => setFollowUpDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              )}

              {/* Treatment Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Treatment Notes
                </label>
                <textarea
                  value={treatmentNotes}
                  onChange={(e) => setTreatmentNotes(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Add any additional notes about the treatment completion..."
                />
              </div>
            </div>

            {/* Modal Actions */}
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowTreatmentCompleteModal(false)}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleTreatmentComplete}
                disabled={!treatmentOutcome}
                className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Complete Treatment
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ========== DocumentPreviewModal =============
function DocumentPreviewModal({ doc, onClose }) {
  let content;
  if (doc.type?.startsWith("image/")) {
    content = (
      <img src={doc.data} alt={doc.name} className="max-h-[75vh] max-w-full mx-auto" />
    );
  } else if (doc.type === "application/pdf") {
    content = (
      <iframe
        src={doc.data}
        title={doc.name}
        className="w-full"
        style={{ height: "75vh", minWidth: 400 }}
      />
    );
  } else if (
    doc.type?.startsWith("text/") ||
    doc.name.endsWith(".txt") ||
    doc.type === ""
  ) {
    content = (
      <iframe
        src={doc.data}
        title={doc.name}
        className="w-full bg-white border rounded"
        style={{ height: "75vh", minWidth: 400 }}
      />
    );
  } else {
    content = (
      <div className="text-center text-gray-600 p-4">
        <span>Cannot preview this file type. Please download to view.</span>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-2xl relative">
        <button
          className="absolute top-2 right-3 text-2xl text-gray-400 hover:text-gray-800"
          onClick={onClose}
          aria-label="Close document preview"
        >
          &times;
        </button>
        <h3 className="text-lg font-bold mb-4">{doc.name}</h3>
        {content}
      </div>
    </div>
  );
}

// Helper function to format dates safely in dd/mm/yyyy format
function formatDateForInvoice(dateValue) {
  if (!dateValue) return "";
  
  let date;
  
  // Handle Firestore Timestamp objects
  if (dateValue.seconds) {
    date = new Date(dateValue.seconds * 1000);
  }
  // Handle Date objects
  else if (dateValue instanceof Date) {
    date = dateValue;
  }
  // Handle string dates
  else if (typeof dateValue === 'string') {
    date = new Date(dateValue);
    if (isNaN(date)) return dateValue;
  }
  else {
    return String(dateValue);
  }
  
  // Format as dd/mm/yyyy
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  
  return `${day}/${month}/${year}`;
}

// Hidden Invoice Component for PDF Generation
function HiddenInvoiceForPDF({ bill, client, appointment, clinicSettings, currentTenant }) {
  if (!bill) return null;

  return (
    <div
      id={`invoice-for-pdf-${bill.id}`}
      className="absolute -left-[9999px] top-0 bg-white"
      style={{ width: '210mm', minHeight: '297mm', padding: '20mm' }}
    >
      {/* Header Section */}
      <div className="flex justify-between items-start mb-8">
        {/* Left: Clinic Logo and Name */}
        <div className="flex items-center space-x-4">
          {clinicSettings?.logo && (
            <img 
              src={clinicSettings.logo} 
              alt="Clinic Logo" 
              className="w-16 h-16 object-contain"
            />
          )}
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {clinicSettings?.clinicName || currentTenant?.name || 'HealeHub Clinic'}
            </h1>
            <p className="text-sm text-gray-600 mt-1">Healthcare Services</p>
          </div>
        </div>
        
        {/* Right: Clinic Address */}
        <div className="text-right text-sm text-gray-600">
          {clinicSettings?.address && (
            <>
              <div>{clinicSettings.address.street}</div>
              <div>{clinicSettings.address.city}, {clinicSettings.address.state} {clinicSettings.address.zipCode}</div>
              {clinicSettings.phone && <div>Tel: {clinicSettings.phone}</div>}
              {clinicSettings.email && <div>Email: {clinicSettings.email}</div>}
            </>
          )}
        </div>
      </div>

      {/* Invoice Title and Details */}
      <div className="border-b-2 border-gray-200 pb-6 mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">INVOICE</h2>
            <div className="text-sm text-gray-600">
              <div>Invoice #: <span className="font-semibold text-gray-900">{bill.invoiceNumber}</span></div>
              <div>Date: <span className="font-semibold text-gray-900">{formatDateForInvoice(bill.createdAt || bill.date)}</span></div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-600 mb-2">Status</div>
            <div className={`inline-flex items-center justify-center px-3 py-1 rounded-full text-sm font-bold ${
              bill.status === 'paid' ? 'bg-emerald-100 text-emerald-800 border border-emerald-200' :
              bill.status === 'unpaid' ? 'bg-red-100 text-red-800 border border-red-200' :
              bill.status === 'void' ? 'bg-gray-100 text-gray-800 border border-gray-200' :
              bill.status === 'superseded' ? 'bg-amber-100 text-amber-800 border border-amber-200' :
              'bg-gray-100 text-gray-800 border border-gray-200'
            }`}>
              {bill.status.toUpperCase()}
            </div>
          </div>
        </div>
      </div>

      {/* Bill To Section */}
      <div className="grid grid-cols-2 gap-8 mb-8">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Bill To:</h3>
          <div className="text-gray-700">
            <div className="font-medium">{bill.clientName || bill.recipientName}</div>
            {bill.recipientEmail && <div className="text-sm text-gray-600">{bill.recipientEmail}</div>}
            {client?.address && (
              <div className="text-sm text-gray-600 mt-2">
                <div>{client.address.street}</div>
                <div>{client.address.city}, {client.address.state} {client.address.zipCode}</div>
              </div>
            )}
          </div>
        </div>
        
        {appointment && (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Appointment Details:</h3>
            <div className="text-gray-700 text-sm">
              <div><span className="font-medium">Date:</span> {formatDateForInvoice(appointment.date)}</div>
              <div><span className="font-medium">Time:</span> {appointment.time}</div>
              <div><span className="font-medium">Service:</span> {appointment.type}</div>
            </div>
          </div>
        )}
      </div>

      {/* Services Table */}
      <div className="mb-8">
        <table className="w-full">
          <thead>
            <tr className="border-b-2 border-gray-200">
              <th className="text-left py-3 font-semibold text-gray-900">Description</th>
              <th className="text-left py-3 font-semibold text-gray-900">Date</th>
              <th className="text-right py-3 font-semibold text-gray-900">Amount</th>
            </tr>
          </thead>
          <tbody>
            {bill.lineItems && bill.lineItems.map((li, idx) => (
              <tr key={idx} className="border-b border-gray-100">
                <td className="py-4 text-gray-700">{li.description}</td>
                <td className="py-4 text-gray-600">{formatDateForInvoice(li.date)}</td>
                <td className="py-4 text-right font-medium text-gray-900">{formatCurrency(li.amount, bill.currency)}</td>
              </tr>
            ))}
            
            {/* Subtotal */}
            <tr className="border-t border-gray-200">
              <td colSpan={2} className="py-2 text-right font-medium text-gray-700">Subtotal:</td>
              <td className="py-2 text-right font-medium text-gray-900">{formatCurrency(bill.subtotal || bill.total, bill.currency)}</td>
            </tr>
            
            {/* Discount */}
            {bill.discount > 0 && (
              <tr>
                <td colSpan={2} className="py-2 text-right font-medium text-gray-700">Discount ({bill.discount}%):</td>
                <td className="py-2 text-right font-medium text-red-600">-{formatCurrency((Number(bill.subtotal || bill.total) * Number(bill.discount) / 100), bill.currency)}</td>
              </tr>
            )}
            
            {/* Tax */}
            {bill.tax > 0 && (
              <tr>
                <td colSpan={2} className="py-2 text-right font-medium text-gray-700">Tax ({bill.tax}%):</td>
                <td className="py-2 text-right font-medium text-gray-900">{formatCurrency((((Number(bill.subtotal || bill.total) - (Number(bill.subtotal || bill.total) * Number(bill.discount || 0) / 100)) * Number(bill.tax)) / 100), bill.currency)}</td>
              </tr>
            )}
            
            {/* Total */}
            <tr className="border-t-2 border-gray-200">
              <td colSpan={2} className="py-4 text-right font-bold text-gray-900 text-lg">Total:</td>
              <td className="py-4 text-right font-bold text-gray-900 text-lg">{formatCurrency(bill.total, bill.currency)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Payment Information */}
      <div className="grid grid-cols-2 gap-8 mb-8">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Payment Information:</h3>
          <div className="text-sm text-gray-700 space-y-1">
            <div><span className="font-medium">Payment Type:</span> {bill.paymentType || 'Not specified'}</div>
            {bill.paymentDate && <div><span className="font-medium">Paid on:</span> {formatDateForInvoice(bill.paymentDate)}</div>}
            {bill.dueDate && <div><span className="font-medium">Due Date:</span> {formatDateForInvoice(bill.dueDate)}</div>}
          </div>
        </div>
        
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Invoice Details:</h3>
          <div className="text-sm text-gray-700 space-y-1">
            <div><span className="font-medium">Issued by:</span> {bill.createdBy || 'System'}</div>
            <div><span className="font-medium">Created:</span> {formatDateForInvoice(bill.createdAt)}</div>
          </div>
        </div>
      </div>

      {/* Payment Details Footer */}
      {clinicSettings?.paymentDetails && (
        <div className="border-t-2 border-gray-200 pt-6 mt-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Payment Details:</h3>
          <div className="text-sm text-gray-700 space-y-1">
            {clinicSettings.paymentDetails.bankName && (
              <div><span className="font-medium">Bank:</span> {clinicSettings.paymentDetails.bankName}</div>
            )}
            {clinicSettings.paymentDetails.accountNumber && (
              <div><span className="font-medium">Account Number:</span> {clinicSettings.paymentDetails.accountNumber}</div>
            )}
            {clinicSettings.paymentDetails.sortCode && (
              <div><span className="font-medium">Sort Code:</span> {clinicSettings.paymentDetails.sortCode}</div>
            )}
            {clinicSettings.paymentDetails.iban && (
              <div><span className="font-medium">IBAN:</span> {clinicSettings.paymentDetails.iban}</div>
            )}
          </div>
        </div>
      )}

      {/* Legal Compliance Footer */}
      {clinicSettings?.invoiceCompliance && (
        <div className="border-t border-gray-200 pt-6 mt-6">
          <div className="text-xs text-gray-600 space-y-2">
            {/* Payment Terms */}
            {clinicSettings.invoiceCompliance.paymentTerms?.enabled && (
              <div>
                <span className="font-medium">Payment Terms:</span> Payment is due within {clinicSettings.invoiceCompliance.paymentTerms.dueDays || 30} days of invoice date. 
                {clinicSettings.invoiceCompliance.paymentTerms.latePaymentRate && (
                  <span> Late payments may incur a {clinicSettings.invoiceCompliance.paymentTerms.latePaymentRate}% monthly charge.</span>
                )}
              </div>
            )}

            {/* Business Registration */}
            {clinicSettings.invoiceCompliance.businessRegistration?.enabled && clinicSettings.invoiceCompliance.businessRegistration?.number && (
              <div>
                <span className="font-medium">Business Registration:</span> {clinicSettings.invoiceCompliance.businessRegistration.number}
              </div>
            )}

            {/* VAT/Tax ID */}
            {clinicSettings.invoiceCompliance.vatTaxId?.enabled && clinicSettings.invoiceCompliance.vatTaxId?.number && (
              <div>
                <span className="font-medium">VAT/Tax ID:</span> {clinicSettings.invoiceCompliance.vatTaxId.number}
              </div>
            )}

            {/* Payment Methods */}
            {clinicSettings.invoiceCompliance.paymentMethods?.enabled && clinicSettings.invoiceCompliance.paymentMethods?.methods && (
              <div>
                <span className="font-medium">Payment Methods Accepted:</span> {clinicSettings.invoiceCompliance.paymentMethods.methods}
              </div>
            )}

            {/* Contact & Dispute Resolution */}
            {clinicSettings.invoiceCompliance.contactDispute?.enabled && (
              <div>
                <span className="font-medium">For billing inquiries or disputes, contact:</span>
                {clinicSettings.invoiceCompliance.contactDispute.email && (
                  <span> {clinicSettings.invoiceCompliance.contactDispute.email}</span>
                )}
                {clinicSettings.invoiceCompliance.contactDispute.phone && (
                  <span> | {clinicSettings.invoiceCompliance.contactDispute.phone}</span>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ClientProfile;